import React, { useState, useEffect, useRef } from "react";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { TextInput, Loader } from "@mantine/core";
import { IconMapPin } from "@tabler/icons-react";
import "./AddressAutocomplete.css";

function AddressAutocomplete({
  value,
  onChange,
  placeholder,
  label,
  required,
}) {
  const [isFocused, setIsFocused] = useState(false);
  const wrapperRef = useRef(null);

  const {
    ready,
    value: searchValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      // Removed type restriction to allow addresses, businesses, and establishments
    },
    debounce: 300,
  });

  // Sync internal search value with external value prop
  useEffect(() => {
    if (value !== searchValue) {
      setValue(value, false);
    }
  }, [value, searchValue, setValue]);

  // Handle clicks outside to close suggestions
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        clearSuggestions();
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [clearSuggestions]);

  const handleInput = e => {
    const newValue = e.target.value;
    setValue(newValue);
    onChange(newValue);
  };

  const handleSelect = description => () => {
    setValue(description, false);
    onChange(description);
    clearSuggestions();
    setIsFocused(false);

    // Optional: Get coordinates if needed in the future
    getGeocode({ address: description })
      .then(results => getLatLng(results[0]))
      .then(({ lat, lng }) => {
        // Could pass this to parent if needed
        // console.log("Coordinates:", { lat, lng });
      })
      .catch(() => {
        // Error getting geocode - silently fail
      });
  };

  const renderSuggestions = () => {
    if (!isFocused || status !== "OK") {
      return null;
    }

    return (
      <div className="address-autocomplete-dropdown">
        {data.map(suggestion => {
          const {
            place_id,
            structured_formatting: { main_text, secondary_text },
          } = suggestion;

          return (
            <div
              key={place_id}
              className="address-autocomplete-item"
              onClick={handleSelect(suggestion.description)}
            >
              <IconMapPin size={16} className="address-autocomplete-icon" />
              <div className="address-autocomplete-text">
                <div className="address-autocomplete-main">{main_text}</div>
                <div className="address-autocomplete-secondary">
                  {secondary_text}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="address-autocomplete-wrapper" ref={wrapperRef}>
      <TextInput
        label={label}
        placeholder={placeholder || "Enter address..."}
        value={searchValue}
        onChange={handleInput}
        onFocus={() => setIsFocused(true)}
        disabled={!ready}
        required={required}
        leftSection={<IconMapPin size={16} />}
        rightSection={!ready ? <Loader size="xs" /> : null}
      />
      {renderSuggestions()}
    </div>
  );
}

export default AddressAutocomplete;
